import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { refreshAccessToken, logout } from "../redux/slices/authSlice";
import { store } from "../redux/store";
import { isTokenValid } from "../utility/token";
import { hasPersistedRefreshToken } from "../utility/authSession";
import LogoFlipLoader from "../components/comman/loading/LogoFlipLoader";
import { motion, AnimatePresence } from "framer-motion";
import { AUTH_ROUTES } from "../config/brand";

const ProtectedRoute = ({ children }) => {
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let mounted = true;

    const ensureAuth = async () => {
      const token = auth?.token;

      if (isTokenValid(token)) {
        if (mounted) setChecking(false);
        return;
      }

      if (!hasPersistedRefreshToken()) {
        dispatch(logout());
        if (mounted) setChecking(false);
        return;
      }

      try {
        await dispatch(refreshAccessToken()).unwrap();
      } catch {
        if (mounted && !isTokenValid(store.getState().auth?.token)) {
          dispatch(logout());
        }
      } finally {
        if (mounted) setChecking(false);
      }
    };

    ensureAuth();

    return () => {
      mounted = false;
    };
  }, [auth?.token, dispatch]);

  if (checking) {
    return (
      <div className="flex justify-center items-center h-screen w-full bg-[#344e41] realtive ">
        <AnimatePresence>
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0 flex flex-col items-center justify-center z-[1000] rounded-2xl"
          >
            <LogoFlipLoader />
            <p className="text-white text-xl mt-2 font-bold animate-pulse">
              Loading...
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  const token = auth?.token;
  const isAuthenticated =
    !!token && isTokenValid(token) && auth.isAuthenticated;

  if (!isAuthenticated) {
    return (
      <Navigate to={AUTH_ROUTES.login} replace state={{ from: location }} />
    );
  }

  return children;
};

export default ProtectedRoute;
